import { WhatsAppContentDefinition, WhatsAppSenderFields } from 'modules/graphql';
import { useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { BasicFilterSelect } from 'ui/selects';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { isDefined } from 'utils/tsHelpers';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useWhatsAppSenderContext } from '../context/WhatsAppSenderContext';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';
import { formatWhatsAppSender, getSelectedSenderLabel, SenderOptionLabel } from '../utils';
import { SenderStatusBadge } from './components/SenderStatusBadge';

const StyledSelect = styled(BasicFilterSelect)`
  #selected-option [data-test="sender-option-label"] {
    justify-content: space-between;
  }
`;

export const WhatsAppSenderSelect = () => {
  const { isWhatsAppGAEnabled } = useWhatsAppMessageChannelFeature();
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { senders } = useWhatsAppSenderContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const [searchInputTerm, setSearchInputTerm] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState(searchInputTerm);

  const { whatsAppSenderId: currentSenderId } = (liveTemplate?.mediumData ||
    {}) as WhatsAppContentDefinition;
  const currentSender = senders.find((sender) => sender.id === currentSenderId);

  const selectedSenderLabel = getSelectedSenderLabel(
    senders,
    currentSenderId,
    SETTINGS_DISPLAY_CONTENT.unsetText,
  );
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const isEditable = isEditModeVisible && isEditableWhatsAppTemplate;

  useDebouncedEffect(() => setSearchTerm(searchInputTerm), [searchInputTerm], {
    skipEffectOnMount: true,
    debounceTimeout: 500,
  });

  const handleSenderChange = (senderId: number) => {
    setCurrentMediumField('whatsAppSenderId', senderId);
  };

  const memoizedOptions = useMemo(() => {
    const options = isDefined<WhatsAppSenderFields[]>(senders)
      ? formatWhatsAppSender(senders, isWhatsAppGAEnabled)
      : [];
    return searchTerm
      ? options.filter(({ label }) =>
          renderToString(label).toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : options;
  }, [searchTerm, senders, isWhatsAppGAEnabled]);

  return (
    <FormField isHorizontalLayout labelText={SETTINGS_DISPLAY_CONTENT.sender.labelText}>
      {isEditable ? (
        <StyledSelect
          data-test="whats-app-select-senders"
          defaultValues={currentSenderId ? [currentSenderId] : []}
          id="whats-app-settings-select-senders"
          onSelect={(vals) => handleSenderChange(vals[0] as number)}
          onSearchChange={(searchedItem) => setSearchInputTerm(searchedItem ?? '')}
          disableInternalFiltering
          options={memoizedOptions}
          placeholder={SETTINGS_DISPLAY_CONTENT.sender.placeholder}
          isFullWidth
        />
      ) : (
        <SettingsReadOnlyText data-test="sender-ro" isUnset={!currentSender}>
          {!!currentSender ? (
            <SenderOptionLabel>
              {selectedSenderLabel}
              {isWhatsAppGAEnabled && (
                <SenderStatusBadge
                  data-test="sender-status-badge-select-readonly"
                  sendLimit={currentSender.sendLimit}
                  status={currentSender.status}
                />
              )}
            </SenderOptionLabel>
          ) : (
            SETTINGS_DISPLAY_CONTENT.unsetText
          )}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
